import React from "react";

import { Col, Container, Form, Row } from "react-bootstrap";

import { FieldError, Resolver } from "react-hook-form";

import { ParticipantTypes } from "@api/graphql";
import { isResaleReporter, isRoyaltyRecipient } from "@common/registration";
import { FormErrorText } from "@components/form/FormErrorText";
import FormNavigationButtons from "@components/form/FormNavigationButtons";
import FormsHeader from "@components/form/FormsHeader";
import FromSwitchOption from "@components/form/FromSwitchOption";
import { useIsInternalUserCreatingAccount } from "@pages/register/hooks/useIsInternalUserCreatingAccount";

import { useRegisterForm } from "../hooks/useRegisterForm";

import { getParticipantOptionsAndHeader } from "./ParticipantOptions";
import { ParticipantTypeInput, ParticipantTypeProps, ValidationErrors } from "./RegisterFormTypes";

export const ParticipantType = ({
  multiFormData,
  onCancel,
  onSubmit,
  isInsertMode = true,
  submitRef,
}: ParticipantTypeProps) => {
  const resolver: Resolver<ParticipantTypeInput> = async (values) => {
    const errors: ValidationErrors<ParticipantTypeInput> = {};

    if (!values.participant_type || values.participant_type.length === 0) {
      errors.participant_type = {
        type: "required",
        message: "At least one option must be selected",
      };
    }

    const isValid = Object.keys(errors).length === 0;

    return {
      values: isValid ? values : {},
      errors: errors,
    };
  };

  const { control, handleSubmit, watch, setValue, clearErrors, errors } = useRegisterForm<ParticipantTypeInput>(
    resolver,
    multiFormData,
  );

  const userRoles = multiFormData.user_role || [];
  const participantType = watch("participant_type") as ParticipantTypes[];
  const handleCancel = () => onCancel && onCancel();

  const toggleParticipantType = (type: ParticipantTypes) => {
    let updatedTypes: ParticipantTypes[];

    if (participantType?.includes(type)) {
      updatedTypes = participantType.filter((t) => t !== type);
    } else if (type === ParticipantTypes.ARTIST || type === ParticipantTypes.BENEFICIARY) {
      updatedTypes =
        participantType?.filter((t) => t === ParticipantTypes.ARTIST || t === ParticipantTypes.BENEFICIARY) || [];
      updatedTypes.push(type);
    } else {
      updatedTypes = [type];
    }

    setValue("participant_type", updatedTypes);
    clearErrors("participant_type");
  };

  const { options, headerTitle } = getParticipantOptionsAndHeader(
    isResaleReporter(userRoles),
    isRoyaltyRecipient(userRoles),
    useIsInternalUserCreatingAccount(),
  );

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <Container>
        <FormsHeader isInsertMode={isInsertMode} title={headerTitle} />
        {userRoles.length === 0 ? (
          <Row className="mt-4 mb-4 text-center">
            <h2>Please complete the previous step before continuing.</h2>
          </Row>
        ) : (
          <Form.Group>
            {options.map((option) => (
              <Row className="mb-4" key={option.id}>
                <FromSwitchOption<ParticipantTypeInput, ParticipantTypes>
                  fieldName="participant_type"
                  control={control}
                  switchId={option.id}
                  label={option.label}
                  description={option.description}
                  toggleSwitch={toggleParticipantType}
                  switchStates={participantType || []}
                  switchKey={option.participantType}
                />
              </Row>
            ))}
            <Row className="mb-4">
              <Col className="d-flex justify-content-center">
                {errors.participant_type && <FormErrorText error={errors.participant_type as FieldError} />}
              </Col>
            </Row>
          </Form.Group>
        )}
        <FormNavigationButtons
          isInsertMode={isInsertMode}
          onSubmit={onSubmit}
          onCancel={handleCancel}
          handleSubmit={handleSubmit}
          submitRef={submitRef}
        />
      </Container>
    </Form>
  );
};
